import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from "@reduxjs/toolkit";
import { getLogin } from "../../services/Login/Login";
import getLoginPublico from "../../services/Login/loginPublico";
import getLocalStorageItem from "../../utils/getLocalStorageItem";
import setLocalStorageItem from "../../utils/setLocalStorageItem";
import { USER_LOCALSTORAGE_KEY } from "../../config/profile";
import getLogout from "../../services/Login/Logout";
import esAdminOConfig from "../../services/Perfil/esAdminOConfig";

const initialState = {
  currentUser: null,
  isPublic: false,
  isLoading: false,
  error: null,
};

export const login = createAsyncThunk(
  "auth/logIn",
  async (userData, thunkAPI) => {
    try {
      const response = await getLogin(userData);
      if (response.isError && response?.data?.message)
        return thunkAPI.rejectWithValue(response?.data?.message);
      if (!response.data.item1)
        return thunkAPI.rejectWithValue("Usuario o contraseña incorrecta");
      const esAdmin = await esAdminOConfig();
      if (esAdmin.isError) {
        return thunkAPI.rejectWithValue(
          "Error al determinar si el usuario es admin o no",
        );
      }
      const user = {
        ...response.data.item1,
        isPublic: false,
        esAdminOConfig: esAdmin.data,
      };
      setLocalStorageItem(USER_LOCALSTORAGE_KEY, user);
      return user;
    } catch (error) {
      console.error("error al logear", error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (userData, thunkAPI) => {
    try {
      await getLogout(userData);
      localStorage.removeItem(USER_LOCALSTORAGE_KEY);
      return {
        ...initialState,
        currentUser: getLocalStorageItem(USER_LOCALSTORAGE_KEY, null),
      };
    } catch (error) {
      console.error("error al logear", error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const loginPublico = createAsyncThunk(
  "auth/logInPublico",
  async (captcha, thunkAPI) => {
    try {
      if (!captcha) {
        return thunkAPI.rejectWithValue("Debe confirmar que no es un Robot");
      }
      const response = await getLoginPublico({ captcha });
      if (!response.data.item1)
        return thunkAPI.rejectWithValue("Error al logear");
      const esAdmin = await esAdminOConfig();
      if (esAdmin.isError) {
        return thunkAPI.rejectWithValue(
          "Error al determinar si el usuario es admin o no",
        );
      }
      const user = {
        ...response.data.item1,
        isPublic: true,
        esAdminOConfig: esAdmin.data,
      };
      setLocalStorageItem(USER_LOCALSTORAGE_KEY, user);
      return user;
    } catch (error) {
      console.error("Error al validar CAPTCHA", error);
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const initialCurrentUser = createAsyncThunk(
  "auth/setCurrentUser",
  async (_, thunkAPI) => {
    try {
      const currentUser = getLocalStorageItem(USER_LOCALSTORAGE_KEY, null);
      return currentUser;
    } catch (error) {
      console.error(
        "error al iniciar el usuario en la primer carga con el localStorage",
        error,
      );
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const hasPermission = createSelector(
  [(state) => state.auth.currentUser],
  (currentUser) =>
    (accessKey1, accessKey2 = null) => {
      if (!currentUser?.perfil?.accesos) return false; // Si no hay accesos, devolvemos false
      const userAccess = currentUser.perfil.accesos;
      const permission = userAccess.some(
        (access) =>
          access.acceso.accesoKey1 === accessKey1 &&
          (!accessKey2 || access.acceso.accesoKey2 === accessKey2),
      );
      // return permission && permission.grantPermition === "1";
      return permission;
    },
);

export const hasUrlAccess = createSelector(
  [(state) => state.menues.menues],
  (menues) => (currentLocation) => {
    let response = {
      hasPermission: false,
      validUrl: "/",
    };
    for (const menu of menues) {
      if (menu.child.find((child) => currentLocation.includes(child.menuUrl))) {
        response.hasPermission = true;
      }
    }
    if (menues?.length) {
      const urlChild = "/" + menues[0]?.child[0]?.menuUrl;
      response.validUrl = urlChild;
    }

    return response;
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateCurrentUser(state, { payload }) {
      state.currentUser = {
        ...state.currentUser,
        ...payload,
      };
      setLocalStorageItem(USER_LOCALSTORAGE_KEY, state.currentUser);
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.currentUser = payload;
        state.isPublic = false;
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      })
      .addCase(logout.fulfilled, (state, { payload }) => {
        state.currentUser = payload.currentUser;
      })
      .addCase(loginPublico.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initialCurrentUser.pending, (state, { payload }) => {
        state.isLoading = true;
      })
      .addCase(initialCurrentUser.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.currentUser = payload;
        state.isPublic = payload?.isPublic;
      })
      .addCase(loginPublico.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.currentUser = payload;
        state.isPublic = true;
      })
      .addCase(loginPublico.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload;
      }),
});

export const { updateCurrentUser } = authSlice.actions;

export default authSlice.reducer;
